import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Grid,
  CircularProgress,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CustomButton from "components/Button";
import {
  useDeleteFileMutation,
  useDeleteFolderMutation,
  useDeletePermissionMutation,
  useCompleteFolderUploadJobMutation,
  useCreateFolderUploadJobMutation,
  useGetFoldersQuery,
  useLazyGetFolderUploadJobQuery,
  useLazyDownloadFileQuery,
  useLazyGetFoldersQuery,
  usePermissionListQuery,
  useUploadFileMutation,
} from "store/slices/apis/dmsSlice";
import { useParams } from "react-router-dom";
import CreateFolder from "./CreateFolder";
import UploadFile from "./UploadFile";
import UploadFolder from "./UploadFolder";
import FolderList from "./FolderList";
import Documents from "./Documents";
import FileVersions from "./FileVersions";
import RenameFolder from "./RenameFolder";
import RenameFile from "./RenameFile";
import moment from "moment";
import ShareFolder from "./ShareFolder";
import ShareFile from "./ShareFile";
import ConfirmationDialog from "components/ConfirmationDialog";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { useDispatch } from "react-redux";
import FolderPermissionModal from "./FolderPermissionModal";
import { getPermissions } from "./dmsPermissions";
import FilePermissionModal from "./FilePermissionModal";
import CommentModal from "./CommentModal";
import UploadNewVersionFile from "./UploadNewVersionFile";
import { useAuth } from "context/AuthContext";
import TrackerPageHeader from "components/TrackerPageHeader";

const subTextStyle = {
  fontSize: "12px",
  color: "#6B7280",
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 B";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
}

function fileTypeFromName(name = "") {
  const ext = name.split(".").pop()?.toLowerCase();
  return ext ? ext.toUpperCase() : "FILE";
}

function fileNameWithoutExtension(name = "") {
  const lastDotIndex = name.lastIndexOf(".");

  if (lastDotIndex === -1 || lastDotIndex === 0) return name;

  return name.substring(0, lastDotIndex);
}

function fileExtension(name = "") {
  const lastDotIndex = name.lastIndexOf(".");

  if (lastDotIndex === -1 || lastDotIndex === 0) return name;

  return name.substring(lastDotIndex);
}

function getUploadedRootFolderName(job) {
  const relativePath = job?.files?.find((file) => file?.relativePath)?.relativePath;
  if (!relativePath) return null;

  return relativePath.split(/[\\/]/).filter(Boolean)[0] || null;
}

/**
 * MVP layout component
 * - Replace mock data with API calls later:
 *   folders -> GET /api/dms/folders/tree
 *   files   -> GET /api/dms/folders/{id}/documents
 *   versions-> GET /api/dms/documents/{documentId}/versions
 */
export default function DMSExplorer() {
  const { projectId } = useParams();
  const { data, isFetching, isError, refetch: refetchRoot } = useGetFoldersQuery({ projectId });
  const [
    trigger,
    { data: lazyData, isFetching: lazyLoading, isError: lazyError },
  ] = useLazyGetFoldersQuery({ projectId });
  const [triggerDownload, { isFetching: dowloadingLoader }] = useLazyDownloadFileQuery();
  const { data: permissionList, isFetching: permissionListLoading, isError: permissionListError, refetch: refetchPermissionList } = usePermissionListQuery();
  const [deletePermissionTrigger] = useDeletePermissionMutation()

  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [search, setSearch] = useState("");
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [isCreateFolder, setIsCreateFolder] = useState(false);
  const [isUploadFile, setIsUploadFile] = useState(false);
  const [isUploadFolder, setIsUploadFolder] = useState(false);
  const [isFolderRename, setIsFolderRename] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [isFileRename, setIsFileRename] = useState(false);
  const [fileToRename, setFileToRename] = useState(null);
  const [loadingFolderId, setLoadingFolderId] = useState(null);
  const [loadingDownloadKey, setLoadingDownloadKey] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState("");
  const [expandedFolderIds, setExpandedFolderIds] = useState([]);
  const [isFolderShare, setIsFolderShare] = useState(false);
  const [folderToShare, setFolderToShare] = useState(null)
  const [isFileShare, setIsFileShare] = useState(false);
  const [fileToShare, setFileToShare] = useState(null);
  const [isConfirm, setIsConfirm] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null)
  const dispatch = useDispatch();
  const [isFolderPermissionModal, setIsFolderPermissionModal] = useState(false);
  const [folderForPermission, setFolderForPermission] = useState(null);
  const [isFilePermissionModal, setIsFilePermissionModal] = useState(false);
  const [fileForPermission, setFileForPermission] = useState(null);
  const [isCommentModal, setIsCommentModal] = useState(false);
  const [isUploadVersion, setIsUploadVersion] = useState(false)
  const { user } = useAuth();
  console.log(user);
  
  const userEmail = user?.email;
  const [isDelete, setIsDelete] = useState(false);
  const [toDeleteData, setToDeleteData] = useState(null);
  const [deleteType, setDeleteType] = useState(null)
  const [isFileNameExist, setIsFileNameExist] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  //console.log("getUserInfo", user, userEmail)

  const [deleteFolderTrigger, { isLoading: deleteFolderLoading, isError: deletFolderError }] = useDeleteFolderMutation()
  const [deleteFileTrigger, { isLoading: deleteFileLoading, isError: deletFileError }] = useDeleteFileMutation()
  const [uploadFile, { isLoading: fileUploading }] = useUploadFileMutation();
  const [createFolderUploadJob] = useCreateFolderUploadJobMutation();
  const [completeFolderUploadJob] = useCompleteFolderUploadJobMutation();
  const [getFolderUploadJob] = useLazyGetFolderUploadJobQuery();

  const getFiles = (folders, selectedFolderId) => {
    const folder = folders.find(
      (folder) => folder.folderId === selectedFolderId
    );

    if (folder) {
      return folder.files;
    }

    for (const f of folders) {
      if (f.children && f.children.length > 0) {
        const foundedFiles = getFiles(f.children, selectedFolderId);
        if (foundedFiles) {
          return foundedFiles;
        }
      }
    }

    return null;
  };

  const files = useMemo(() => {
    const allFiles = getFiles(folders, selectedFolderId) || [];
    const list = allFiles.map((item) => ({ ...item, id: item.fileId }));
    if (!search?.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((f) => (f.fileName || "").toLowerCase().includes(q));
  }, [search, selectedFolderId, folders]);


  const selectedDoc = useMemo(() => {
    const list = files || [];
    return list.find((f) => f.fileId === selectedDocId) || null;
  }, [selectedFolderId, selectedDocId, files]);


  const handleDownload = async (fileId, version, fileName, key) => {
    console.log(fileId, version, fileName, key)

    try {
      setLoadingDownloadKey(key)
      const blob = await triggerDownload({ fileId, version }).unwrap();
      console.log("download blob", blob.fileName);

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'download');
      document.body.appendChild(link);

      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      openNotification("Downloaded Successfully", "success");
      //handleCloseMenu();
    } catch (error) {
      console.error("Download failed:", error);
      openNotification(error?.data?.message || "Download Failed");
    } finally {
      setLoadingDownloadKey(null)
    }
  };

  const handleUpload = () => {
    // TODO: open upload modal (new doc OR new version)
    setIsUploadFile(true);
  };

  const handleFolderUpload = () => {
    setIsUploadFolder(true);
  };

  const handleNewFolder = () => {
    // TODO: open create folder modal
    setIsCreateFolder(true);
  };

  const handleRollback = (version) => {
    // TODO: POST /api/dms/documents/{id}/rollback/{version}
    console.log("rollback", selectedDocId, version);
  };

  // const insertChildByFolderId = (folders, folderId, childrenData) => {
  //   return folders.map((folder) => {
  //     if (folder.folderId === folderId) {
  //       return { ...folder, children: childrenData };
  //     }

  //     if (folder?.children?.length > 0) {
  //       return {
  //         ...folder,
  //         children: insertChildByFolderId(
  //           folder.children,
  //           folderId,
  //           childrenData
  //         ),
  //       };
  //     }
  //     return folder;
  //   });
  // };

  const replaceNodeById = (nodes, updatedNode) => {
    return nodes.map((node) => {
      if (node.folderId === updatedNode.folderId) {
        return updatedNode;
      }

      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: replaceNodeById(node.children, updatedNode),
        };
      }

      return node;
    });
  };

  const handleFolderClick = async (folder, id) => {
    const folderId = folder?.folderId || id;


    // Stop if leaf
    if (folder?.isLastLeaf) return;

    // Stop if already loaded
    if (folder?.children?.length > 0) return;

    setLoadingFolderId(folderId);

    try {
      const result = await trigger({ projectId, parentId: folderId }).unwrap();

      // console.log("result", result.data)

      const findedNode = result?.data?.find(
        (item) => item.folderId === folderId
      );
      //console.log("result nodes", findedNode)


      if (!findedNode) return;

      setFolders((prev) => replaceNodeById(prev, findedNode));

    } catch (error) {
      console.error("Fetch failed", error);
    } finally {
      setLoadingFolderId(null);
    }
  };

  const handleOpenRename = (folder) => {
    setFolderToRename(folder);
    setIsFolderRename(true);
  };

  const handleOpenCreateFolder = (folder) => {
    setSelectedFolderId(folder.folderId)
    setSelectedFolderName(folder?.folderName)
    setFolderToRename(folder);
    setIsCreateFolder(true);
  };

  const handleFileRename = (file) => {
    setFileToRename(file);
    setIsFileRename(true)
  }

  useEffect(() => {
    if (data?.data) {
      setFolders(data.data);
    }
  }, [data]);

  //console.log("Folders", folders)

  const refreshFolderData = async (folderId) => {
    if (!folderId) {
      await refetchRoot();
      return;
    }

    setLoadingFolderId(folderId);

    try {
      const result = await trigger({ projectId, parentId: folderId }).unwrap();

      const findedNode = result?.data?.find(
        (item) => item.folderId === folderId
      );

      if (!findedNode) return null;

      setFolders((prev) => replaceNodeById(prev, findedNode));
      return findedNode;

    } catch (error) {
      console.error("Refresh failed:", error);
      return null;
    } finally {
      setLoadingFolderId(null);
    }
  };

  const handleFolderUploadSuccess = async (jobId, completedJob) => {
    openNotification("Folder upload completed", "success");

    const refreshedParent = await refreshFolderData(selectedFolderId);
    const uploadedRootFolderName = getUploadedRootFolderName(completedJob);
    const uploadedFolder = refreshedParent?.children?.find(
      (folder) => folder.folderName === uploadedRootFolderName
    );

    if (uploadedFolder) {
      setExpandedFolderIds((prev) => Array.from(new Set([...prev, selectedFolderId])));
      setSelectedFolderId(uploadedFolder.folderId);
      setSelectedFolderName(uploadedFolder.folderName);
      setSelectedDocId(null);
    }

    refetchPermissionList();
  };

  const handleShareFolder = (folder) => {
    setFolderToShare(folder);
    setIsFolderShare(true)
  }
  const handleShareFile = (file) => {
    setFileToShare(file);
    setIsFileShare(true)
  }

  const handleConfirmationClose = () => {
    setIsConfirm(false);
    setPermissionToDelete(null)
  };

  const handleConfirmDelete = async () => {
    if (!permissionToDelete) {
      console.warn("No permission to delete")
      return
    }
    if (!permissionToDelete?.permissionId && !permissionToDelete?.resourceType) {
      console.warn("No permission id and resource type is found")
      return
    }
    const type = permissionToDelete?.resourceType?.toLowerCase()
    try {
      const requestBody = {
        permissionId: permissionToDelete?.permissionId,
        type: type
      }
      const result = await deletePermissionTrigger(requestBody).unwrap();
      console.log("result", result)
      if (result?.status === "Success") {
        openNotification(`${permissionToDelete?.resourceType} Permission Deleted Successfully`, "success")
        refetchPermissionList()
        handleConfirmationClose()
        setIsFolderPermissionModal(false)
        setIsFilePermissionModal(false)
      } else {
        openNotification("Fail to Delete Permission");
      }
    } catch (error) {
      console.log("delete permission error", error);
      openNotification(error?.data?.message || "Fail to Delete Permission");
    }

  };

  const onFolderPermissionManageClick = (folder) => {
    console.log("folder to revoke permission", folder)
    setFolderForPermission(folder);
    setIsFolderPermissionModal(true);
  };

  const onFilePermissionManageClick = (file) => {
    console.log("file to revoke permission", file)
    setFileForPermission(file);
    setIsFilePermissionModal(true);
  };

  const onFolderDeleteClick = (folder) => {
    setDeleteType("folder")
    setToDeleteData(folder);
    setIsDelete(true);
  }

  const onFileDeleteClick = (file) => {
    setDeleteType("file")
    setToDeleteData(file);
    setIsDelete(true);
  }

  const handleDeleteConfirmationClose = () => {
    setDeleteType(null);
    setToDeleteData(null);
    setIsDelete(false);
  }

  const handleConfirmDeleteFolderOrFile = () => {
    if (deleteType === "folder") {
      handleFolderDelete(toDeleteData)
    } else if (deleteType === "file") {
      handleFileDelete(toDeleteData)
    } else {
      console.log("toDeleteData", toDeleteData)
    }

  }

  const handleFolderDelete = async (data) => {
    console.log("folder delete triggered", data)

    try {
      const folderId = data?.folderId;
      const parentFolderId = data?.parentFolderId
      if (!folderId) {
        console.log("No folder id to delete")
        return
      }
      const result = await deleteFolderTrigger(folderId).unwrap();
      if (result?.status === "Success") {
        openNotification(`Folder Deleted Successfully`, "success")
        // parent folder id is passed as current folder will delete current folder
        refreshFolderData(parentFolderId)
        handleDeleteConfirmationClose()
      }
    } catch (err) {
      openNotification(err?.data?.message || `Folder Deletion Failed`)
      console.log("Delete folder err", err)
    }
  };

  const handleFileDelete = async (data) => {
    console.log("file delete triggered", data)
    try {
      const fileId = data?.fileId;
      if (!fileId) {
        console.log("No file id to delete")
        return
      }
      const result = await deleteFileTrigger(fileId).unwrap();
      if (result?.status === "Success") {
        openNotification(`File Deleted Successfully`, "success")
        refreshFolderData(selectedFolderId)
        handleDeleteConfirmationClose()
      }
    } catch (err) {
      openNotification(err?.data?.message || `File Deletion Failed`)
      console.log("Delete file err", err)
    }
  }

  const handleUploadFile = async (file, resetForm) => {
    try {
      const formData = new FormData();
      formData.append("FolderId", selectedFolderId);
      formData.append("File", file);

      const result = await uploadFile(formData).unwrap();

      if (result?.status === "Success") {
        openNotification("File uploaded successfully", "success");
        refetchPermissionList()
        refreshFolderData(selectedFolderId)
        resetForm?.();
        //handleClose();
        if (isFileNameExist) {
          setIsFileNameExist(false)
        }
        setIsUploadFile(false)
      }

    } catch (error) {
      openNotification(error?.data?.message || "Upload failed");
    }
  };




  const openNotification = (msg, type = "error", duration = 5000) => {
    dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: 'alert',
        alert: {
          color: type
        },
        close: false
      })
    );
    setTimeout(() => dispatch(closeSnackbar()), duration);
  };

  const isAdmin = useMemo(() => {
    if (!user?.roles) return false;

    return user?.roles?.some(role => role.toLowerCase() === "admin" || role.toLowerCase() === "system admin");
  }, [user])

  console.log("ISADMIN", isAdmin)


  const permissions = getPermissions(selectedFolderId, "Folder", permissionList?.data, userEmail, isAdmin)

  // console.log("permissions", permissions, selectedFolderId)

  const columns = useMemo(
    () => [
      {
        field: "actions",
        name: "",
        width: 10,
        sticky: true,
        // sortable: false,
        // filterable: false,
        // renderer: (p) => (
        //   <Box
        //     sx={{
        //       display: "flex",
        //       alignItems: "center",
        //       justifyContent: "center",
        //     }}
        //   >
        //     <IconButton size="small" onClick={(e) => handleOpenMenu(e, p.id)}>
        //       <MoreVertIcon fontSize="small" />
        //     </IconButton>
        //   </Box>
        // ),
        // renderer added in child
      },
      {
        field: "fileName",
        name: "Name",
        flex: 1.6,
        minWidth: 220,
        renderCell: (params) => (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <DescriptionOutlinedIcon fontSize="small" />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }} noWrap>
                {params.value}
              </Typography>
              <Typography sx={subTextStyle} noWrap>
                {fileTypeFromName(params.value)}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "currentVersion",
        name: "Version",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => <Chip size="small" label={`v${p.value}`} />,
      },
      {
        field: "fileSize",
        name: "Size",
        width: 110,
        renderer: (p) => formatBytes(p?.fileSize),
      },
      { field: "createdBy", name: "Uploaded By", width: 140 },
      {
        field: "createdDate", name: "Date", width: 120, renderer: (p) => (
          <Typography sx={{ fontSize: "13px" }}>
            {p?.createdDate ? moment(p?.createdDate).format("DD-MM-YYYY") : "-"}
          </Typography>
        )
      },
    ],
    []
  );

  if (isFetching && folders.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <Typography color="error">
          Failed to load folders. Please refresh.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TrackerPageHeader title="Document Management" description="Browse project folders, upload documents, and manage file versions.">
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          {permissions?.canAddFolder && <CustomButton label="Add New Folder" startIcon={<CreateNewFolderOutlinedIcon />} color="primary" onClick={handleNewFolder} sx={{ backgroundColor: "#2563EB", fontFamily: "Outfit", fontWeight: 500, fontSize: "14px" }} />}
          {permissions?.canUpload && <CustomButton label="Upload" startIcon={<CloudUploadOutlinedIcon />} color="primary" onClick={handleUpload} disabled={!selectedFolderId} sx={{ backgroundColor: "#2563EB", fontFamily: "Outfit", fontWeight: 500, fontSize: "14px" }} />}
          {permissions?.canUploadFolder && <CustomButton label="Upload Folder" startIcon={<CloudUploadOutlinedIcon />} color="primary" onClick={handleFolderUpload} disabled={!selectedFolderId} sx={{ backgroundColor: "#2563EB", fontFamily: "Outfit", fontWeight: 500, fontSize: "14px" }} />}
        </Stack>
      </TrackerPageHeader>
      <Grid
        container
        spacing={2}
        sx={{ p: 2, boxSizing: "border-box" }}
      >
        <Grid size={12} sx={{ p: 0 }}>
          <Box sx={{ height: "calc(100vh - 150px)" }}>
            <Stack direction="row" spacing={2} sx={{ height: "100%" }}>
              <FolderList
                folders={folders}
                handleFolderClick={handleFolderClick}
                selectedFolderId={selectedFolderId}
                setSelectedFolderId={setSelectedFolderId}
                setSelectedDocId={setSelectedDocId}
                onRenameClick={handleOpenRename}
                loadingFolderId={loadingFolderId}
                expandedFolderIds={expandedFolderIds}
                setExpandedFolderIds={setExpandedFolderIds}
                onCreateFolder={handleOpenCreateFolder}
                setSelectedFolderName={setSelectedFolderName}
                onShareClick={handleShareFolder}
                onManageFolderPermission={onFolderPermissionManageClick}
                permissionList={permissionList?.data || []}
                userEmail={userEmail}
                onDeleteFolder={onFolderDeleteClick}
                isAdmin={isAdmin}
              />
              <Documents
                setSelectedDocId={setSelectedDocId}
                columns={columns}
                selectedDocId={selectedDocId}
                files={files}
                selectedFolderId={selectedFolderId}
                handleDownload={handleDownload}
                handleFileRename={handleFileRename}
                selectedFolderName={selectedFolderName}
                onShareClick={handleShareFile}
                onManageFilePermission={onFilePermissionManageClick}
                permissionList={permissionList?.data || []}
                userEmail={userEmail}
                onDeleteFile={onFileDeleteClick}
                isAdmin={isAdmin}
                search={search}
                setSearch={setSearch}
              />
              <FileVersions
                selectedDoc={selectedDoc}
                handleDownload={handleDownload}
                handleUpload={() => setIsUploadVersion(true)}
                handleRollback={handleRollback}
                loadingKey={loadingDownloadKey}
                selectedFolderName={selectedFolderName}
                permissionList={permissionList?.data || []}
                onOpenCommentModal={(() => setIsCommentModal(true))}
                userEmail={userEmail}
                isAdmin={isAdmin}
              />
            </Stack>
          </Box>



        </Grid>
      </Grid>
      {isCreateFolder && (
        <CreateFolder
          selectedFolderId={selectedFolderId}
          open={isCreateFolder}
          handleClose={() => setIsCreateFolder(false)}
          onSuccess={() => {
            handleFolderClick({}, selectedFolderId, true);
            refetchPermissionList()
          }}
        />
      )}
      {isUploadFile && (
        <UploadFile
          //selectedFolderId={selectedFolderId}
          open={isUploadFile}
          handleClose={() => setIsUploadFile(false)}
          // onSuccess={() => {
          //   refetchPermissionList()
          //   refreshFolderData(selectedFolderId)
          // }}
          files={files}
          setIsFileNameExist={setIsFileNameExist}
          onUpload={handleUploadFile}
          setPendingFile={setPendingFile}
          isLoading={fileUploading}
        />
      )}
      {isUploadFolder && (
        <UploadFolder
          open={isUploadFolder}
          handleClose={() => setIsUploadFolder(false)}
          projectId={projectId}
          parentFolderId={selectedFolderId}
          createJob={createFolderUploadJob}
          completeJob={completeFolderUploadJob}
          getJob={getFolderUploadJob}
          onSuccess={handleFolderUploadSuccess}
        />
      )}
      {isFolderRename && (
        <RenameFolder
          open={isFolderRename}
          handleClose={() => setIsFolderRename(false)}
          folderId={folderToRename?.folderId}
          currentName={folderToRename?.folderName}
          //onSuccess={() => handleFolderClick({}, folderToRename?.folderId, true)}
          onSuccess={() => refreshFolderData(folderToRename?.parentFolderId || null)}
        />
      )}
      {isFileRename && (
        <RenameFile
          open={isFileRename}
          handleClose={() => setIsFileRename(false)}
          fileId={fileToRename?.fileId}
          currentName={fileNameWithoutExtension(fileToRename?.fileName)}
          onSuccess={() => refreshFolderData(selectedFolderId)}
          extension={fileExtension(fileToRename?.fileName)}
        />
      )}
      {isFolderShare && (
        <ShareFolder
          open={isFolderShare}
          handleClose={() => setIsFolderShare(false)}
          folderId={folderToShare?.folderId}
          onSuccess={() => {
            refetchPermissionList()
          }}
        />
      )}
      {isFileShare && (
        <ShareFile
          open={isFileShare}
          handleClose={() => setIsFileShare(false)}
          fileId={fileToShare?.fileId}
          onSuccess={() => {
            refetchPermissionList()
          }}
        />
      )}
      {isConfirm && (
        <ConfirmationDialog
          open={isConfirm}
          handleClose={handleConfirmationClose}
          handleConfirm={handleConfirmDelete}
          actionButtonName={"Delete"}
          title={`Are You Sure You want to Delete?`}
          description={`This action will delete this ${permissionToDelete?.resourceType} Permission. This cannot be undone.`}
        />
      )}
      {isFolderPermissionModal && (
        <FolderPermissionModal
          open={isFolderPermissionModal}
          onClose={() => setIsFolderPermissionModal(false)}
          folderId={folderForPermission?.folderId}
          permissionList={permissionList?.data || []}
          onDelete={(permission) => {
            setPermissionToDelete(permission);
            setIsConfirm(true);
          }}
          userEmail={userEmail}
        />
      )}
      {isFilePermissionModal && (
        <FilePermissionModal
          open={isFilePermissionModal}
          onClose={() => setIsFilePermissionModal(false)}
          fileId={fileForPermission?.fileId}
          permissionList={permissionList?.data || []}
          onDelete={(permission) => {
            setPermissionToDelete(permission);
            setIsConfirm(true);
          }}
          userEmail={userEmail}
        />
      )}

      <CommentModal
        open={isCommentModal}
        onClose={() => setIsCommentModal(false)}
        fileId={selectedDocId}
      />


      <UploadNewVersionFile
        selectedFileId={selectedDocId}
        open={isUploadVersion}
        handleClose={() => setIsUploadVersion(false)}
        onSuccess={() => {
          refetchPermissionList()
          refreshFolderData(selectedFolderId)
        }}
      />

      {isDelete && (
        <ConfirmationDialog
          open={isDelete}
          handleClose={handleDeleteConfirmationClose}
          handleConfirm={handleConfirmDeleteFolderOrFile}
          actionButtonName={"Delete"}
          title={`Are You Sure You want to Delete?`}
          description={`This action will delete this ${deleteType}. This cannot be undone.`}
        />
      )}

      {isFileNameExist && (
        <ConfirmationDialog
          open={isFileNameExist}
          handleClose={() => setIsFileNameExist(false)}
          handleConfirm={() => handleUploadFile(pendingFile?.file, pendingFile?.resetForm)}
          actionButtonName={"Upload"}
          title="File Already Exists"
          description="A file with the same name already exists in this folder. Uploading this file will create a new version of the existing file."
        />
      )}

    </>
  );
}
